/**
 * Deep Insights Service
 * =====================
 * Universal client for AI-powered trading analysis.
 *
 * This service provides a single, intelligent interface for all analysis types:
 * - Pattern detection
 * - Conversational coaching
 * - Personalized suggestions
 * - Learning plans
 * - Time-based analysis
 * - Risk behavior analysis
 * - Performance analysis
 * - General search
 *
 * All API keys are kept server-side for security.
 * See: docs/SUPABASE_SETUP.md
 *
 * Usage:
 * ```javascript
 * import { getDeepInsights } from '../services/deepInsights';
 *
 * const response = await getDeepInsights('Why do I lose on Fridays?');
 * console.log(response.answer);
 * console.log(response.patterns);
 * ```
 */

import * as React from 'react';
import { getApiUrl } from '../api/config';

// =============================================================================
// CONFIGURATION
// =============================================================================

const getDeepInsightsUrl = () => {
  return `${getApiUrl()}/ai/deep-insights`;
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getDefaultFollowUps(type) {
  const followUps = {
    pattern_observation: [
      'How can I fix my biggest pattern issue?',
      'Create a plan to address these patterns',
      'Show me examples of when this pattern appeared'
    ],
    reflective_coaching: [
      'Tell me more about my risk management',
      'How can I improve my exit timing?',
      'What should I focus on this week?'
    ],
    behavioral_observations: [
      'Create a learning plan based on these observations',
      'Which observation should I prioritize?',
      'Show me data supporting these recommendations'
    ],
    self_reflection_plan: [
      'Break down the daily actions in more detail',
      'What resources can help with this plan?',
      'How do I track my progress?'
    ],
    time_analysis: [
      'Why do I perform differently on certain days?',
      'Should I avoid trading at certain times?',
      'How can I optimize my trading schedule?'
    ],
    risk_awareness: [
      'How can I prevent over-leveraging?',
      'Create a risk management checklist',
      'Show me my worst risk management trades'
    ],
    performance_review: [
      "What's driving my improvement/decline?",
      'Compare my recent performance to earlier',
      'What should I focus on to improve?'
    ],
    search: [
      'Analyze these results for patterns',
      'What can I learn from these trades?',
      'Show me similar trades'
    ],
    trade_categorization: [
      'Show me similar trades with this strategy',
      'How does this strategy perform overall?',
      'What patterns exist in these trade types?'
    ]
  };

  return followUps[type] || followUps.search;
}

// =============================================================================
// MAIN API FUNCTION
// =============================================================================

/**
 * Get deep insights for any trading-related question.
 *
 * This is the main function that replaces all separate AI analysis endpoints.
 * Simply ask a question in natural language, and the AI will:
 * 1. Analyze the intent of your question
 * 2. Fetch the relevant data
 * 3. Generate comprehensive insights
 *
 * @param {string} question - Natural language question about trading
 * @param {Array} conversationHistory - Optional previous conversation for context
 * @returns {Promise<Object>} DeepInsightsResponse
 *
 * @example
 * // Pattern detection
 * const response = await getDeepInsights('What patterns do you see in my trading?');
 *
 * @example
 * // Coaching with context
 * const history = [{ role: 'user', content: 'Why do I lose?' }, { role: 'assistant', content: '...' }];
 * const response = await getDeepInsights('Tell me more about that', history);
 */
export async function getDeepInsights(question, conversationHistory = []) {
  const token = localStorage.getItem('access_token');

  // Validate user authentication
  if (!token) {
    return {
      success: false,
      type: 'error',
      question,
      answer: 'Please log in to use Deep Insights.',
      error: 'User not authenticated',
      follow_up_suggestions: []
    };
  }

  try {
    const response = await fetch(getDeepInsightsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        question,
        conversation_history: conversationHistory
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();

    // Ensure follow-up suggestions exist
    if (!data.follow_up_suggestions) {
      data.follow_up_suggestions = getDefaultFollowUps(data.type);
    }

    return data;
  } catch (error) {
    console.error('[DeepInsights] Error:', error);
    return {
      success: false,
      type: 'error',
      question,
      answer: `Unable to process your request: ${error.message}`,
      error: error.message,
      follow_up_suggestions: []
    };
  }
}

/**
 * Categorize a trade from its notes using AI.
 *
 * @param {string} notes - Trade notes/journal entry
 * @param {Object} tradeData - Basic trade data (symbol, direction, profit, etc.)
 * @returns {Promise<Object>} TradeCategorizationResult
 */
export async function categorizeTrade(notes, tradeData) {
  const token = localStorage.getItem('access_token');

  if (!token) {
    console.error('[DeepInsights] User not authenticated');
    return {
      strategy: 'unknown',
      market_condition: 'unknown',
      risk_type: 'unknown',
      setup_quality: 'unknown',
      confidence: 0,
      error: 'User not authenticated'
    };
  }

  try {
    const response = await fetch(getDeepInsightsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        question: 'Categorize this trade',
        trade_data: { notes, tradeData }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.categorization || {
      strategy: 'unknown',
      market_condition: 'unknown',
      risk_type: 'unknown',
      setup_quality: 'unknown',
      confidence: 0
    };
  } catch (error) {
    console.error('[DeepInsights] Trade categorization error:', error);
    return {
      strategy: 'unknown',
      market_condition: 'unknown',
      risk_type: 'unknown',
      setup_quality: 'unknown',
      confidence: 0,
      error: error.message
    };
  }
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Get behavioral patterns in trading history.
 */
export async function getPatterns() {
  return getDeepInsights('What behavioral patterns do you see in my trading history?');
}

/**
 * Get personalized improvement suggestions.
 */
export async function getSuggestions() {
  return getDeepInsights('What should I focus on to improve my trading?');
}

/**
 * Get a learning plan for a specific timeframe.
 * @param {string} timeframe - 'week' or 'month'
 */
export async function getLearningPlan(timeframe = 'week') {
  return getDeepInsights(`Create a ${timeframe}ly learning and improvement plan for me`);
}

/**
 * Analyze performance on a specific day of the week.
 * @param {string} day - Day name (e.g., 'Friday')
 */
export async function analyzeDayPerformance(day) {
  return getDeepInsights(`How do I perform on ${day}s? Analyze my trading patterns for that day.`);
}

/**
 * Analyze risk behavior patterns.
 */
export async function analyzeRiskBehavior() {
  return getDeepInsights('Do I over-leverage after losses? Analyze my risk management patterns.');
}

/**
 * Get performance trend analysis.
 */
export async function getPerformanceTrend() {
  return getDeepInsights('Am I improving over time? Analyze my performance trends.');
}

/**
 * Ask a coaching question.
 * @param {string} question - The question to ask
 * @param {Array} conversationHistory - Previous conversation for context
 */
export async function askCoach(question, conversationHistory = []) {
  return getDeepInsights(question, conversationHistory);
}

// =============================================================================
// REACT HOOK
// =============================================================================

/**
 * React hook for using deep insights with loading and error states.
 *
 * @example
 * ```jsx
 * import { useDeepInsights } from '../services/deepInsights';
 *
 * function AnalysisComponent() {
 *   const { data, loading, error, ask, conversationHistory, clearHistory } = useDeepInsights();
 *
 *   return (
 *     <div>
 *       <button onClick={() => ask('What patterns do you see?')}>
 *         Analyze
 *       </button>
 *       {loading && <Spinner />}
 *       {error && <Error message={error} />}
 *       {data && <Results data={data} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useDeepInsights() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [conversationHistory, setConversationHistory] = React.useState([]);

  const ask = React.useCallback(async (question) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getDeepInsights(question, conversationHistory);
      setData(response);

      if (!response.success) {
        setError(response.error || 'Request failed');
      } else if (response.type === 'reflective_coaching') {
        // Update conversation history for reflective coaching mode
        setConversationHistory(prev => [
          ...prev,
          { role: 'user', content: question },
          { role: 'assistant', content: response.answer }
        ]);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [conversationHistory]);

  const clearHistory = React.useCallback(() => {
    setConversationHistory([]);
  }, []);

  const reset = React.useCallback(() => {
    setData(null);
    setError(null);
    setConversationHistory([]);
  }, []);

  return { data, loading, error, conversationHistory, ask, clearHistory, reset };
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  getDeepInsights,
  categorizeTrade,
  getPatterns,
  getSuggestions,
  getLearningPlan,
  analyzeDayPerformance,
  analyzeRiskBehavior,
  getPerformanceTrend,
  askCoach,
  useDeepInsights
};
